import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  MenuItemAnchor,
  MenuItemLink,
  Typography,
  useTheme,
} from '@freighthub/silhouette-core'
import { Avatar, getInitials, getUserType, IAvatarProps, INITIALS_AVATAR } from 'features/ActivityFeed'
import { useIntegrationsEnabled } from 'features/Integrations'
import { FC, MouseEvent, useState } from 'react'
import { authorize } from 'service/roles'
import { useAuth } from 'service/useAuth'
import { useI18n } from 'utils/localisation'
import { Access } from 'view/Access'

import { getHoverColor } from './TopBarHoverStlyes'
import { TopBarMenuLanguageSelect } from './TopBarMenuLanguageSelect'

const getStyles = (theme) => ({
  iconButton: {
    height: 32,
    width: 32,
    marginLeft: theme.spacing(1),
    padding: 0,
    '&:hover': {
      backgroundColor: getHoverColor(theme),
    },
  },
  menu: {
    marginTop: theme.spacing(1),
  },
  avatarName: {
    marginLeft: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  helpLink: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
})

export const TopBarMenu: FC = (props) => {
  const styles = getStyles(useTheme())
  const { translate } = useI18n()
  const {
    state: { user, mainOrganization },
    logout,
  } = useAuth()
  const [anchorEl, setAnchorEl] = useState(null)

  const userName: string = user ? `${user.firstName} ${user.lastName}` : ''
  const avatarProps: IAvatarProps = {
    avatar: user ? getInitials(user) : '',
    avatarType: INITIALS_AVATAR,
    userType: getUserType(user._id),
  }
  const handleClickAnchorElement = (event: MouseEvent<HTMLButtonElement>) => {
    // @ts-expect-error TS(2345) FIXME: Argument of type 'EventTarget & HTMLButtonElement'... Remove this comment to see the full error message
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    handleClose()
    logout()
  }

  const isIntegrationsEnabled = useIntegrationsEnabled()

  return (
    <>
      <IconButton
        onClick={handleClickAnchorElement}
        sx={styles.iconButton}
        data-userlane-id="header-icon-user"
        {...props}
      >
        <Avatar {...avatarProps} size="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        width="auto"
        sx={styles.menu}
      >
        <MenuItemLink to="/settings" onClick={handleClose}>
          <Box display="flex" alignItems="center">
            <Avatar {...avatarProps} size="small" />
            <Typography sx={styles.avatarName}>{userName}</Typography>
          </Box>
        </MenuItemLink>
        <MenuItemLink to="/settings" onClick={handleClose} data-userlane-id="header-icon-user-settings">
          {translate('header-my_account')}
        </MenuItemLink>
        <MenuItemLink
          to="/notification-center/settings"
          onClick={handleClose}
          data-userlane-id="header-icon-notification-settings"
        >
          {translate('company_users-notification_settings')}
        </MenuItemLink>
        <TopBarMenuLanguageSelect onClose={handleClose} />
        <Access roles={authorize.managers}>
          <Divider />
          <MenuItemLink to="/company/users" onClick={handleClose} data-userlane-id="header-icon-user-users">
            {translate('menu-company_account')}
          </MenuItemLink>
          <MenuItemLink
            to={`/directory/organization/${mainOrganization}`}
            onClick={handleClose}
            data-userlane-id="header-icon-user-company"
          >
            {translate('header-company')}
          </MenuItemLink>
          <MenuItemLink to="/company/documents" onClick={handleClose} data-userlane-id="header-icon-user-documents">
            {translate('menu-company_documents')}
          </MenuItemLink>
          <MenuItemLink to="/company/activity" onClick={handleClose} data-userlane-id="header-icon-user-activity">
            {translate('menu-company_activity')}
          </MenuItemLink>
          <MenuItemLink to="/directory" onClick={handleClose} data-userlane-id="header-icon-user-directory">
            {translate('menu-directory')}
          </MenuItemLink>
          {isIntegrationsEnabled && (
            <MenuItemLink to="/integrations" onClick={handleClose} data-userlane-id="header-icon-integrations">
              {translate('menu-integrations')}
            </MenuItemLink>
          )}
        </Access>
        <Divider />
        <MenuItemLink to="/support" onClick={handleClose}>
          {translate('menu-support')}
        </MenuItemLink>
        <MenuItemAnchor href={translate('help_center-url')} target="_blank" sx={styles.helpLink}>
          {translate('menu-help')}
        </MenuItemAnchor>
        <MenuItem onClick={handleLogout}>{translate('header-logout')}</MenuItem>
      </Menu>
    </>
  )
}
